<script>
import gql from "graphql-tag";
import GlobalSearchResult from "@/components/support/GlobalSearchResult.vue";

export default {
  name: "GlobalSearch",
  components: {GlobalSearchResult},

  data: () => ({
    descriptionLimit: 60,
    selectedResult: {},
    search: null,
    showResultMenu: false,
    facilities: [],
    facilityComplexes: [],
    landEntities: [],
    projects: [],
    designGuidelines: [],
    frontEndDocuments: [],
    technicalStandards: [],
    standardDetails: []
  }),

  methods: {
    showSearchResultDialog() {
      if (this.selectedResult !== null) {
        this.showResultMenu = true
      }
    }
  },

  computed: {
    items() {
      const formatItem = (item) => {
        let text = `${item.building || item.projectNo || item.csiSubDivisionCode || ''} ${item.name || item.csiSubDivision || item.description || ''}`
        const Description =
            text.length > this.descriptionLimit
                ? `${text.slice(0, this.descriptionLimit)}...`
                : `${text}`;
        return {...item, Description};
      };

      const formattedProjects = this.projects.map(formatItem);
      const formattedFacilities = this.facilities.map(formatItem);
      const formattedLandEntities = this.landEntities.map(formatItem);
      const formattedFacilityComplexes = this.facilityComplexes.map(formatItem);
      const formattedDesignGuidelines = this.designGuidelines.map(formatItem);
      const formattedFrontEndDocuments = this.frontEndDocuments.map(formatItem);
      const formattedTechnicalStandards = this.technicalStandards.map(formatItem);
      const formattedStandardDetails = this.standardDetails.map(formatItem);

      const mergedItems = [
        ...formattedFacilities,
        ...formattedFacilityComplexes,
        ...formattedLandEntities,
        ...formattedProjects,
        ...formattedDesignGuidelines,
        ...formattedFrontEndDocuments,
        ...formattedTechnicalStandards,
        ...formattedStandardDetails,
      ];

      const sortItems = (a, b) => {
        if (a.Description > b.Description) return 1;
        if (b.Description > a.Description) return -1;
        return 0;
      };

      return mergedItems.sort(sortItems);
    },
  },

  apollo: {
    projects: {
      query: gql`
        query ($searchInput: FacilityProjectFilterInput) {
          facilityProject(where: $searchInput) {
            projectId
            projectNo
            name
          }
        }
      `,
      update: (data) => data.facilityProject,
      variables() {
        let searchTerm = this.search.toUpperCase();

        let searchInput = {
          searchInput: {
            or: [
              {name: {contains: searchTerm}},
              {projectNo: {contains: searchTerm}},
              {relatedFacilityNos: {contains: searchTerm}},
              {relatedProjects: {contains: searchTerm}}
            ],
          },
        };

        if (!isNaN(parseInt(searchTerm))) {
          searchInput.searchInput.or.push({
            projectId: {in: parseInt(searchTerm)},
          });
        }
        return searchInput;
      },
      skip() {
        return !this.search || this.search.length === 0;
      },
    },

    facilities: {
      query: gql`
        query ($searchInput: FacilityFilterInput) {
          facility (where: $searchInput) {
            facilityId
            building
            name
            aliases
            status
            owned
            zone
            buildingType
            buildingUsage
            structureType
            yearBuilt
            yearRazed
            grossSqft
          }
        }
      `,
      update: (data) => data.facility,
      variables() {
        let searchTerm = this.search.toUpperCase();

        return {
          searchInput: {
            or: [
              {name: {contains: searchTerm}},
              {abbreviation: {contains: searchTerm}},
              {building: {contains: searchTerm}},
              {officialName: {contains: searchTerm}},
              {aliases: {contains: searchTerm}}
            ],
          },
        }
      },
      skip() {
        return !this.search || this.search.length === 0;
      },
    },

    landEntities: {
      query: gql`
        query ($searchInput: LandEntityFilterInput) {
          landEntity (where: $searchInput) {
            facilityId
            building
            name
          }
        }
      `,
      update: (data) => data.landEntity,
      variables() {
        let searchTerm = this.search.toUpperCase();

        return {
          searchInput: {
            or: [
              {name: {contains: searchTerm}},
              {building: {contains: searchTerm}}
            ],
          },
        }
      },
      skip() {
        return !this.search || this.search.length === 0;
      },
    },

    facilityComplexes: {
      query: gql`
        query ($searchInput: FacilityComplexFilterInput) {
          facilityComplex (where: $searchInput) {
            complexId
            building
            name
          }
        }
      `,
      update: (data) => data.facilityComplex,
      variables() {
        let searchTerm = this.search.toUpperCase();

        return {
          searchInput: {
            or: [
              {name: {contains: searchTerm}},
              {building: {contains: searchTerm}}
            ],
          },
        }
      },
      skip() {
        return !this.search || this.search.length === 0;
      },
    },

    designGuidelines: {
      query: gql`
        query ($searchInput: DesignGuidelineFilterInput) {
          designGuideline (where: $searchInput) {
            description
            pdfDownloadUrl
            docxDownloadUrl
          }
        }
      `,
      update: (data) => data.designGuideline,
      variables() {
        let searchTerm = this.search.toUpperCase();

        return {
          searchInput: {
            description: {contains: searchTerm}
          },
        }
      },
      skip() {
        return !this.search || this.search.length === 0;
      },
    },

    frontEndDocuments: {
      query: gql`
        query ($searchInput: FrontEndDocumentFilterInput) {
          frontEndDocument (where: $searchInput) {
            description
            pdfDownloadUrl
            docxDownloadUrl
          }
        }
      `,
      update: (data) => data.frontEndDocument,
      variables() {
        let searchTerm = this.search.toUpperCase();

        return {
          searchInput: {
            description: {contains: searchTerm}
          },
        }
      },
      skip() {
        return !this.search || this.search.length === 0;
      },
    },

    technicalStandards: {
      query: gql`
        query ($searchInput: TechnicalStandardFilterInput) {
          technicalStandard (where: $searchInput) {
            csiSubDivision
            csiSubDivisionCode
            pdfDownloadUrl
            docxDownloadUrl
          }
        }
      `,
      update: (data) => data.technicalStandard,
      variables() {
        let searchTerm = this.search.toUpperCase();

        return {
          searchInput: {
            or: [
              {csiSubDivisionCode: {contains: searchTerm}},
              {csiSubDivision: {contains: searchTerm}}
            ],
          },
        }
      },
      skip() {
        return !this.search || this.search.length === 0;
      },
    },

    standardDetails: {
      query: gql`
        query ($searchInput: StandardDetailFilterInput) {
          standardDetail (where: $searchInput) {
            csiSubDivision
            csiSubDivisionCode
            pdfDownloadUrl
            dwgDownloadUrl
          }
        }
      `,
      update: (data) => data.standardDetail,
      variables() {
        let searchTerm = this.search.toUpperCase();

        return {
          searchInput: {
            or: [
              {csiSubDivisionCode: {contains: searchTerm}},
              {csiSubDivision: {contains: searchTerm}}
            ],
          },
        }
      },
      skip() {
        return !this.search || this.search.length === 0;
      },
    },

  }
}
</script>

<template>
  <div class="fill-height d-flex align-center justify-center pt-2" style="width: 650px">
    <v-menu offset-y v-model="showResultMenu" nudge-top="25">
      <template v-slot:activator="{attrs}">
        <v-autocomplete dense filled
                        dark
                        clearable
                        full-width
                        v-model="selectedResult"
                        :items="items"
                        :loading="$apollo.loading"
                        :search-input.sync="search"
                        color="white"
                        hide-no-data
                        hide-selected
                        item-text="Description"
                        label="Buildings, Land Entities, Projects, Construction Standards..."
                        placeholder="Start typing to Search"
                        append-icon="mdi-database-search"
                        return-object
                        @change="showSearchResultDialog()"
                        v-bind="attrs"></v-autocomplete>
      </template>
      <global-search-result ref="searchResultDialog" :result="selectedResult"></global-search-result>
    </v-menu>


  </div>
</template>
